import React, { useState, useEffect } from 'react';
import { Search, Plus, X, Save, Activity } from 'lucide-react';
import { to5Scale, render5Scale } from '../utils/displayConverters';

const API_BASE = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api';

/**
 * Editor for managing linked Sex Actions
 */
const SexActionEditor = ({ kink, onClose, onSave }) => {
    const [availableActions, setAvailableActions] = useState([]);
    const [selectedActions, setSelectedActions] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);

    // Load initial state and available actions
    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await fetch(`${API_BASE}/sex-actions`);
                const allActions = await res.json();
                setAvailableActions(allActions);

                // Initialize selected from kink prop
                if (kink.sexActions) {
                    setSelectedActions(kink.sexActions.map(a => ({
                        id: a.id,
                        name: a.name,
                        intensity: a.intensity,
                        note: a.KinkSexAction?.note || ''
                    })));
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [kink]);

    const handleAdd = (action) => {
        if (selectedActions.find(a => a.id === action.id)) return;
        setSelectedActions([...selectedActions, { ...action, note: '' }]);
        setSearch('');
    };

    const handleRemove = (id) => {
        setSelectedActions(selectedActions.filter(a => a.id !== id));
    };

    const handleNoteChange = (id, note) => {
        setSelectedActions(selectedActions.map(a =>
            a.id === id ? { ...a, note } : a
        ));
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const payload = selectedActions.map(a => ({ id: a.id, note: a.note }));

            const res = await fetch(`${API_BASE}/kinks/${kink.id}/actions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ actions: payload })
            });

            if (!res.ok) throw new Error('Failed to save');

            onSave(); // Trigger refresh
            onClose();
        } catch (err) {
            alert('Error saving actions');
            setLoading(false);
        }
    };

    // Filter available actions
    const filtered = availableActions
        .filter(a => !selectedActions.find(s => s.id === a.id))
        .filter(a => a.name.toLowerCase().includes(search.toLowerCase()))
        .slice(0, 10); // Limit results

    if (loading && !availableActions.length) return <div className="p-4 text-white">Loading...</div>;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="bg-dark-900 border border-dark-700 rounded-xl w-full max-w-2xl flex flex-col max-h-[90vh] shadow-2xl">

                {/* Header */}
                <div className="p-6 border-b border-dark-800 flex justify-between items-center bg-dark-950/50 rounded-t-xl">
                    <h2 className="text-xl font-display font-bold text-white flex items-center gap-2">
                        <Activity className="w-5 h-5 text-wine-500" />
                        Edit Core Actions
                    </h2>
                    <button onClick={onClose} className="text-dark-400 hover:text-white transition-colors" aria-label="Close editor">
                        <X className="w-6 h-6" aria-hidden="true" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">

                    {/* Search/Add */}
                    <div className="relative">
                        <div className="flex items-center gap-2 bg-dark-800 border border-dark-700 rounded-lg px-3 py-2 focus-within:border-wine-500/50 transition-colors">
                            <Search className="w-5 h-5 text-dark-400" />
                            <input
                                type="text"
                                placeholder="Search actions to add..."
                                className="bg-transparent border-none focus:outline-none text-white w-full placeholder-dark-500"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>

                        {/* Dropdown Results */}
                        {search && filtered.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-2 bg-dark-900 border border-dark-700 rounded-lg shadow-xl z-10 overflow-hidden">
                                {filtered.map(action => (
                                    <button
                                        key={action.id}
                                        onClick={() => handleAdd(action)}
                                        className="w-full text-left px-4 py-3 hover:bg-dark-800 flex items-center justify-between group transition-colors"
                                    >
                                        <span className="text-gray-200 group-hover:text-white">{action.name}</span>
                                        <span className="text-xs text-dark-500 uppercase font-bold">{action.category}</span>
                                        <Plus className="w-4 h-4 text-dark-400 group-hover:text-wine-500" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Selected List */}
                    <div className="space-y-3">
                        <h3 className="text-sm font-bold uppercase text-dark-400 tracking-wider">Linked Actions</h3>
                        {selectedActions.length === 0 ? (
                            <div className="text-dark-500 italic p-4 text-center border border-dashed border-dark-800 rounded-lg">
                                No actions linked yet.
                            </div>
                        ) : (
                            selectedActions.map(action => (
                                <div key={action.id} className="flex items-start gap-3 bg-dark-800/30 p-3 rounded-lg border border-dark-800 group hover:border-dark-700 transition-colors">
                                    <div className="w-8 h-8 rounded-full bg-dark-900 border border-dark-700 flex items-center justify-center text-xs font-mono text-wine-400 shrink-0" title={`Intensity: ${action.intensity}/100`}>
                                        {to5Scale(action.intensity * 10)}
                                    </div>
                                    <div className="flex-1 space-y-2">
                                        <div className="flex justify-between items-start">
                                            <span className="font-bold text-gray-200">{action.name}</span>
                                            <button onClick={() => handleRemove(action.id)} className="text-dark-500 hover:text-red-400 transition-colors" aria-label={`Remove ${action.name}`}>
                                                <X className="w-4 h-4" aria-hidden="true" />
                                            </button>
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="Add context note (e.g. 'Core mechanic')"
                                            value={action.note || ''}
                                            onChange={e => handleNoteChange(action.id, e.target.value)}
                                            className="w-full bg-dark-950/50 border border-dark-800 rounded px-2 py-1 text-xs text-wine-100 placeholder-dark-600 focus:border-wine-500/30 focus:outline-none transition-colors"
                                        />
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-dark-800 bg-dark-950/50 rounded-b-xl flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-dark-300 hover:text-white transition-colors">Cancel</button>
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className="flex items-center gap-2 px-6 py-2 bg-wine-600 hover:bg-wine-500 text-white font-bold rounded-lg transition-colors shadow-lg shadow-wine-900/20 disabled:opacity-50"
                    >
                        <Save className="w-4 h-4" />
                        Save Changes
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SexActionEditor;
